import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import RoleBasedSidebar from '../components/RoleBasedSidebar';
import '../styles/SettingsPage.css';
import '../styles/DashboardLayout.css';
import settingsService from '../services/settingsService';
import api from '../api/api';

const SettingsPage = () => {
  const { user, forceLogout } = useContext(AuthContext);
  const [settings, setSettings] = useState({
    notifications: {
      email: true,
      browser: true
    },
    theme: 'light',
    language: 'en',
    centerInfo: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Toggle sidebar
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Close sidebar
  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Get user preferences
        try {
          const preferencesData = await settingsService.getUserPreferences();
          const settingsData = preferencesData.success 
            ? preferencesData.data 
            : preferencesData;
          
          setSettings(prev => ({
            ...prev,
            ...settingsData
          }));
        } catch (prefError) {
          console.error('Error fetching preferences:', prefError);
        }

        // Get notification settings
        try {
          const notificationData = await settingsService.getNotificationSettings();
          
          setSettings(prev => ({
            ...prev,
            notifications: {
              email: notificationData?.messages || true,
              browser: notificationData?.groupMessages || true,
              individualMessages: notificationData?.individualMessages || true,
              groupMessages: notificationData?.groupMessages || true
            }
          }));
        } catch (notifError) {
          console.error('Error fetching notification settings:', notifError);
        }
        
        // If admin or super_admin, get center info
        if (user && (user.role === 'admin' || user.role === 'super_admin') && user.center) {
          try {
            const centerResponse = await api.get(`/centers/${user.center}`);
            
            // Debug log to see the full response structure
            console.log('CENTER API RESPONSE:', JSON.stringify(centerResponse, null, 2));
            console.log('CENTER API RESPONSE DATA:', JSON.stringify(centerResponse.data, null, 2));
            
            if (centerResponse.data) {
              const centerData = centerResponse.data.success 
                ? centerResponse.data.data 
                : centerResponse.data;
                
              // Debug log to see what we're actually using from the response
              console.log('EXTRACTED CENTER DATA:', JSON.stringify(centerData, null, 2));
              
              // Check if centerData has the expected properties
              console.log('Center properties check:', {
                centerCode: centerData.centerCode,
                contactEmail: centerData.contactEmail,
                contactPhone: centerData.contactPhone,
                name: centerData.name,
                address: centerData.address
              });
              
              // Create a cleaned version of the center data with explicit property mapping
              const cleanedCenterData = {
                centerCode: centerData.centerCode || centerData.code || '',
                contactEmail: centerData.contactEmail || centerData.email || '',
                contactPhone: centerData.contactPhone || centerData.phone || '',
                name: centerData.name || '',
                address: centerData.address || ''
              };
              
              console.log('Using cleaned center data:', cleanedCenterData);
              
              setSettings(prev => ({
                ...prev,
                centerInfo: cleanedCenterData
              }));
            }
          } catch (centerError) {
            console.error('Error fetching center data:', centerError);
          }
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
        setError('Failed to load settings. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchSettings();
    }
  }, [user]);

  const handleToggleChange = (category, setting) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: !prev[category][setting]
      }
    }));
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Theme and language settings are currently disabled
      // await settingsService.updateUserPreferences({
      //   theme: settings.theme,
      //   language: settings.language
      // });
      
      // Save notification settings - we only use email flag for notifications on web
      await settingsService.updateNotificationSettings({
        messages: settings.notifications.email,
        individualMessages: settings.notifications.browser, // Use browser setting for both individual and group
        groupMessages: settings.notifications.browser,      // Use browser setting for both individual and group
        assignments: true,  // Default value based on mobile implementation
        assessments: false  // Default value based on mobile implementation
      });
      
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      {/* Mobile Header - without hamburger menu */}
      <div className="mobile-header">
        <h1 className="app-title">Settings</h1>
        <div className="avatar">👤</div>
      </div>

      {/* Dashboard Layout */}
      <div className="dashboard-layout">
        {/* Sidebar */}
        <RoleBasedSidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        
        {/* Main Content */}
        <div className="dashboard-content">
          <div className="settings-container">
            <div className="header">
              <div className="header-left">
                <Link to="/dashboard" className="back-to-dashboard">
                  &larr; Back to Dashboard
                </Link>
                <h1>Settings</h1>
              </div>
            </div>

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading settings...</p>
        </div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="settings-content">
          {saved && (
            <div className="success-message">Settings saved successfully!</div>
          )}
          
          <div className="settings-card">
            <h2>Application Settings</h2>
            
            
            <div className="settings-section">
              <h3>Notifications</h3>

              <div className="setting-item checkbox">
                <div>
                  <label htmlFor="email-notifications">Email Notifications</label>
                  <small>Receive notifications via email</small>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    id="email-notifications"
                    checked={settings.notifications.email}
                    onChange={() => handleToggleChange('notifications', 'email')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item checkbox">
                <div>
                  <label htmlFor="browser-notifications">Browser Notifications</label>
                  <small>Show notifications for messages and updates in your browser</small>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    id="browser-notifications"
                    checked={settings.notifications.browser}
                    onChange={() => handleToggleChange('notifications', 'browser')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="settings-section">
              <h3>Account</h3>

              <div className="setting-item">
                <div className="user-profile-container">
                  <div>
                    <h4>User Profile</h4>
                    <small>View and edit your personal information, contact details, and password.</small>
                  </div>
                  <Link to="/profile" className="view-profile-btn">
                    View Profile
                  </Link>
                </div>
              </div>

              <div className="setting-item">
                <div className="force-logout-container">
                  <div>
                    <h4>Clear App Data</h4>
                    <small>This will completely clear all local data and log you out. Use this if you're experiencing login issues.</small>
                  </div>
                  <button
                    className="force-logout-btn"
                    onClick={() => {
                      if (window.confirm('Are you sure? This will clear all local data and log you out.')) {
                        forceLogout();
                      }
                    }}
                  >
                    Force Logout
                  </button>
                </div>
              </div>
            </div>
            
            {/* Admin-specific settings */}
            {user && (user.role === 'admin' || user.role === 'super_admin') && (
              <div className="settings-section">
                <h3>Center Information</h3>
                
                <div className="center-info">
                  <div className="info-item">
                    <span className="label">Name:</span>
                    <span className="value">{settings.centerInfo.name || 'Not set'}</span>
                  </div>
                  
                  <div className="info-item">
                    <span className="label">Code:</span>
                    <span className="value">{settings.centerInfo.centerCode || 'Not set'}</span>
                  </div>
                  
                  <div className="info-item">
                    <span className="label">Email:</span>
                    <span className="value">{settings.centerInfo.contactEmail || 'Not set'}</span>
                  </div>
                  
                  <div className="info-item">
                    <span className="label">Phone:</span>
                    <span className="value">{settings.centerInfo.contactPhone || 'Not set'}</span>
                  </div>
                  
                  <div className="info-item">
                    <span className="label">Address:</span>
                    <span className="value">{settings.centerInfo.address || 'Not set'}</span>
                  </div>
                </div>
                
                <Link to="/center/edit" className="edit-center-btn">
                  Edit Center Information
                </Link>
              </div>
            )}
            
            <div className="settings-actions">
              <button 
                className="save-btn"
                onClick={handleSaveSettings}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
            
            {/* Logout button at bottom of settings page */}
            <div className="logout-section">
              <button 
                className="logout-btn"
                onClick={() => {
                  if (window.confirm('Are you sure you want to log out?')) {
                    forceLogout();
                  }
                }}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;